// eslint-disable-next-line import/no-extraneous-dependencies
import { ToastOptions } from 'react-toastify';
import { ReactNode } from 'react';
import {
  AgreementsEventNames,
  emitEvent,
  EventTypes,
  LinkInteractionEventNames,
  NotificationInteractionEventNames,
  SearchInteractionEventNames,
} from '@pmi/analytics-layer';
import { toast } from '@pmi/dsm-react';
import { ErrorToast, SuccessToast } from './components/Toast';

export const toastWithAnalytics = (
  message: string | ReactNode,
  variant?: null | 'success' | 'danger' | 'warning' | 'info',
  reportingMessage?: string,
  toastId?: string,
  options?: ToastOptions,
): void => {
  const ToastElement = variant === 'danger' ? ErrorToast : SuccessToast;
  toast(
    undefined,
    variant,
    ToastElement({ message }),
    undefined,
    toastId,
    options,
  );

  if (variant === 'danger') {
    emitEvent({
      eventType: EventTypes.NotificationInteraction,
      eventName: NotificationInteractionEventNames.ErrorLoad,
      eventData: {
        message: reportingMessage || message,
      },
    });
  }
};

export const signalRToastWithAnalytics = (
  message: string | ReactNode,
  variant?: null | 'success' | 'danger' | 'warning' | 'info',
  reportingMessage?: string,
  toastId?: string,
  options?: ToastOptions,
): void => {
  const handleClickOutside = () => {
    setTimeout(() => {
      // Bypass when clicked on the menu container
      const openedSignalRToasts = document.querySelectorAll(
        '.toast--signalr button',
      );
      if (openedSignalRToasts.length > 0) {
        openedSignalRToasts.forEach((element) => {
          (element as HTMLElement).click();
        });
      }
    }, 5000);
  };

  const onToastOpen = () => {
    document.addEventListener('click', handleClickOutside, true);
  };

  const onToastClose = () => {
    document.removeEventListener('click', handleClickOutside, true);
  };

  const signalRToastOptions: ToastOptions = {
    autoClose: false,
    className: `toast--${variant} toast--signalr`,
    onOpen: onToastOpen,
    onClose: onToastClose,
    ...options,
  };

  toastWithAnalytics(
    message,
    variant,
    reportingMessage,
    toastId,
    signalRToastOptions,
  );
};

export const reportError = (message: string, delay: number = 0): void => {
  setTimeout(
    () =>
      emitEvent({
        eventType: EventTypes.NotificationInteraction,
        eventName: NotificationInteractionEventNames.ErrorLoad,
        eventData: { message },
      }),
    delay,
  );
};

export const reportLinkClick = (
  linkTitle: string,
  targetURL?: string,
  linkModule?: string,
  isSearchResult?: boolean,
): void => {
  let cleanTitle = linkTitle.replace(/<.*?>(.*?)<\/.*?>/gi, ''); // html tags
  // eslint-disable-next-line no-control-regex
  cleanTitle = cleanTitle.replace(/[^\x00-\x7F]/g, ''); // html entities
  cleanTitle = cleanTitle.replace(/\s+/g, ' ').trim(); // spaces
  cleanTitle = cleanTitle.replace(/,/g, '');

  if (isSearchResult) {
    emitEvent({
      eventType: EventTypes.SearchInteraction,
      eventName: SearchInteractionEventNames.SearchClick,
      eventData: {
        searchClick: {
          targetLinkTitle: cleanTitle,
          targetLinkModule: linkModule,
          targetURL,
        },
      },
    });
  } else {
    emitEvent({
      eventType: EventTypes.LinkInteraction,
      eventName: LinkInteractionEventNames.LinkClick,
      eventData: {
        link: {
          linkTitle: cleanTitle,
          targetURL,
          linkModule,
        },
      },
    });
  }
};

export const reportSearch = (eventData: any): void => {
  emitEvent({
    eventType: EventTypes.SearchInteraction,
    eventName: SearchInteractionEventNames.SearchLoadB2B,
    eventData,
  });
};

export const reportCoachMarks = (
  linkTitle: string,
  linkModule?: string,
): void => {
  emitEvent({
    eventType: EventTypes.LinkInteraction,
    eventName: LinkInteractionEventNames.LinkClick,
    eventData: {
      link: {
        linkTitle,
        linkModule,
      },
    },
  });
};

export const reportNumberOfDeals = (
  accountId: string,
  noOfAgreements: number,
): void => {
  if (accountId) {
    // @ts-ignore
    if (window?.adobeDataLayer && Array.isArray(window?.adobeDataLayer)) {
      // @ts-ignore
      // eslint-disable-next-line no-unsafe-optional-chaining
      const adobe: [] = [...window?.adobeDataLayer];
      // @ts-ignore
      const foundIndex = adobe.findIndex((a) => a?.event === 'agreements');
      if (foundIndex >= 0) {
        return;
      }
    }
    emitEvent({
      eventType: EventTypes.Agreements,
      eventName: AgreementsEventNames.Agreements,
      eventData: {
        event: 'agreements',
        eventInfo: 'agreements',
        accountdetails: {
          noOfAgreements: noOfAgreements.toString(),
          accountid: accountId,
        },
      },
    });
  }
};
